import { lazy, Suspense, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentGroups, TranslationNamespaces } from '~/lib/constants';
import { Tagged } from '~/apps/smartsend/components/Tagged';
import { P2GBox } from '@parcel2go/core-ui';
import { ContentContext, useAnalytics } from '@parcel2go/npm-p2g-app';
import {
  RoutingConstants,
  RoutingConstantsLabels,
  RoutingConstantsTabs
} from '~/apps/smartsend/lib/consts/RoutingConstants';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { NavigationContext } from '~/components/nav/nav-context';
import useOnLoad from '../use-on-load';
import { GetDefaultSubItem, SubNavButtonGroup, SubNavItemType } from '../groups/sub-nav';

const UserSettings = lazy(() => import('~/apps/user-settings'));
const CommunicationPreferences = lazy(() => import('~/apps/communication-preferences/communication-preferences'));
const MyDetails = lazy(() => import('~/apps/my-details/my-details'));

const options: SubNavItemType[] = [
  {
    translationKey: 'TAB_PROFILE_LABEL',
    label: RoutingConstantsLabels[RoutingConstantsTabs.Profile],
    tab: RoutingConstantsTabs.Profile
  },
  {
    translationKey: 'TAB_COMMUNICATION_PREFERENCES_LABEL',
    label: 'Communications',
    tab: RoutingConstantsTabs.CommunicationPreferences
  }
  // {
  //   translationKey: 'TAB_SETTINGS_LABEL',
  //   label: 'User Settings',
  //   tab: RoutingConstantsTabs.Settings
  // }
] as const;

export default function Component() {
  const { track } = useAnalytics();
  const { dispatch } = useContext(ContentContext);
  const { dispatch: navigationDispatch } = useContext(NavigationContext);
  const { t } = useTranslation(TranslationNamespaces.SETTINGS);
  const [selectedOption, setSelectedOption] = useState<string>(GetDefaultSubItem(options));

  useEffect(() => {
    if (selectedOption) {
      window.location.hash = selectedOption;

      track({
        type: 'page_view',
        payload: {
          ...GenerateBaseAnalyticsPayload([]),
          page: selectedOption,
          area: RoutingConstants.Settings
        }
      });
    }
  }, [selectedOption, track]);

  useOnLoad(() => {
    const title = t('SETTINGS_PAGE_TITLE', { defaultValue: 'Settings' });
    document.title = title;
    dispatch({ type: 'SET_CONTENT_GROUP', contentGroup: ContentGroups.SETTINGS });
    navigationDispatch({
      type: 'INITIALISE_NAVIGATION',
      payload: { title: title, currentPageRoute: RoutingConstants.Settings }
    });
  });

  return (
    <Tagged tag="screen-settings">
      <P2GBox display="flex" flexDirection="column" gap={4}>
        <SubNavButtonGroup
          options={options}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          translationNameSpace={TranslationNamespaces.MYDETAILS}
        />

        {selectedOption === RoutingConstantsTabs.CommunicationPreferences && (
          <Suspense>
            <CommunicationPreferences />
          </Suspense>
        )}
        {selectedOption === RoutingConstantsTabs.Profile && (
          <Suspense>
            <MyDetails />
          </Suspense>
        )}
        {selectedOption === RoutingConstantsTabs.Settings && (
          <Suspense>
            <UserSettings />
          </Suspense>
        )}
      </P2GBox>
    </Tagged>
  );
}

Component.displayName = 'User Settings';
